/**
 * Compose attachment shapes (027 → 051).
 *
 * `AttachmentRef` is the metadata-only shape that crosses the OMA+ ↔ API
 * boundary in the `submitMessage` tRPC payload (name/type/size — no bytes).
 *
 * `ComposeAttachment` (051) pairs that ref with the real `File` so the send
 * flow can, AFTER the case is created, upload the actual bytes to it via the
 * reused member-cases upload path. The `File` is client-only and NEVER travels
 * over tRPC — the submit payload carries `ref` alone. Held in memory only; no
 * browser-local persistence (051 FR-011).
 */

import type { AttachmentRef } from './MessageComposePort.js';

/** A compose attachment: the wire metadata (`ref`) plus the real bytes (`file`). */
export interface ComposeAttachment {
  readonly ref: AttachmentRef;
  readonly file: File;
}

/**
 * Build the metadata-only wire ref (no bytes). Retained as the shared wire-ref
 * builder — now called internally by `toComposeAttachment`; kept exported for
 * reuse by any caller needing the tRPC-payload shape without the File.
 */
export function toClientAttachmentRef(file: File): AttachmentRef {
  return {
    id: `client:${file.name}`,
    fileName: file.name,
    contentType: file.type,
    sizeBytes: file.size,
  };
}

/** Build a compose attachment carrying both the wire ref and the real File (051). */
export function toComposeAttachment(file: File): ComposeAttachment {
  return { ref: toClientAttachmentRef(file), file };
}
